// The nudge: a small toast after an action moved a badge goal ("2 more coffees").
// It sits where the reveal tiles sit (bottom right, above the bottom navigation on
// a phone) and above them when they are on screen. It leaves after six seconds or
// on a tap, which opens the badge in the collection. Hovering or focusing it holds
// the clock; while stashed (a menu, a sheet or a big reveal is open) it is hidden
// and the clock stops.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { haptic } from "../../../utils/haptics";
import { BadgeArt } from "../art";
import { useReducedMotion } from "../reveal/hooks";
import type { BadgeNudge } from "../types";
import GoalBar from "./GoalBar";
import { goalLabel, goalLine } from "./goalFormat";
import "./badgeNudge.css";

/** How long a nudge stays up, ms. */
export const NUDGE_SHOW_MS = 6000;
/** The least time left after the clock was held (so it does not vanish the moment a sheet closes), ms. */
export const NUDGE_RESUME_MS = 2000;
const LEAVE_MS = 200;
/** The reveal tiles, which the nudge stacks on top of. */
const TILE_STACK_SELECTOR = ".bdg-tiles";
const STACK_GAP = 10;

export interface BadgeNudgeToastProps {
  nudge: BadgeNudge;
  /** Hidden, and the clock stops: floating chrome or a card or cinematic is open. */
  stashed?: boolean;
  /** Sit above the siteline dock chip (the desktop home page), as the tiles do. */
  aboveDock?: boolean;
  /** The reveal tiles are on screen: sit on top of them. */
  overTiles?: boolean;
  /** A tap: the host opens the collection. */
  onOpen: (nudge: BadgeNudge) => void;
  /** The six seconds ran out. */
  onDone: () => void;
}

/** Calls `onDone` once `ms` of unheld time has passed. */
function useShowClock(ms: number, held: boolean, onDone: () => void): void {
  const done = useRef(onDone);
  useEffect(() => {
    done.current = onDone;
  }, [onDone]);
  const left = useRef(ms);
  const wasHeld = useRef(false);
  useEffect(() => {
    if (held) {
      wasHeld.current = true;
      return undefined;
    }
    const wait = wasHeld.current ? Math.max(left.current, NUDGE_RESUME_MS) : left.current;
    left.current = wait;
    const started = Date.now();
    const t = window.setTimeout(() => done.current(), wait);
    return () => {
      window.clearTimeout(t);
      left.current = Math.max(0, left.current - (Date.now() - started));
    };
  }, [held]);
}

/**
 * The bottom offset, px, that puts the nudge on top of the element `selector`
 * finds (a fixed element anchored to the bottom), or null to keep the CSS spot.
 */
function useStackOn(selector: string, active: boolean): number | null {
  const [bottom, setBottom] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (!active || typeof document === "undefined") {
      setBottom(null);
      return undefined;
    }
    const el = document.querySelector<HTMLElement>(selector);
    if (!el) {
      setBottom(null);
      return undefined;
    }
    const measure = () => {
      const base = parseFloat(window.getComputedStyle(el).bottom);
      setBottom(Number.isFinite(base) && el.offsetHeight > 0 ? Math.round(base + el.offsetHeight + STACK_GAP) : null);
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [selector, active]);
  return bottom;
}

const BadgeNudgeToast: React.FC<BadgeNudgeToastProps> = ({
  nudge,
  stashed = false,
  aboveDock = false,
  overTiles = false,
  onOpen,
  onDone,
}) => {
  const reduced = useReducedMotion();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const leaveTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(leaveTimer.current), []);

  const leave = useCallback(() => {
    if (reduced) {
      onDone();
      return;
    }
    setLeaving(true);
    leaveTimer.current = window.setTimeout(onDone, LEAVE_MS);
  }, [reduced, onDone]);

  useShowClock(NUDGE_SHOW_MS, stashed || hovered || focused || leaving, leave);
  const stackBottom = useStackOn(TILE_STACK_SELECTOR, overTiles);

  const next = nudge.next;
  const classes = [
    "bdg-nudge",
    aboveDock ? "bdg-nudge--above-dock" : "",
    reduced ? "bdg-nudge--reduced" : "",
    stashed ? "is-stashed" : "",
    leaving ? "is-leaving" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const node = (
    <div
      className={classes}
      style={stackBottom !== null ? { bottom: stackBottom } : undefined}
      role="status"
      aria-hidden={stashed || undefined}
      data-testid="badge-nudge"
    >
      <button
        type="button"
        className="bdg-nudge__btn"
        aria-label={goalLabel(nudge)}
        tabIndex={stashed ? -1 : undefined}
        onClick={() => {
          haptic("light");
          onOpen(nudge);
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      >
        <BadgeArt
          category={next.category}
          tier={next.tier}
          number={next.number}
          code={next.code}
          name={next.name}
          size={32}
          variant="mini"
          locked
        />
        <span className="bdg-nudge__text">
          <span className="bdg-nudge__name">{next.name}</span>
          <span className="bdg-nudge__line">{goalLine(nudge)}</span>
          <GoalBar goal={nudge} className="bdg-nudge__bar" />
        </span>
      </button>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(node, document.body) : node;
};

export default BadgeNudgeToast;
